import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, BadgeCheck, TrendingUp, Flame, Sparkles, Calendar, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useBuyerFeePct } from "../lib/useBuyerFee";
import { Button } from "../components/ui/button";
import AuctionCard from "../components/AuctionCard";

export default function Home() {
  const buyerFee = useBuyerFeePct();
  const [featured, setFeatured] = useState([]);
  const [endingSoon, setEndingSoon] = useState([]);
  const [newest, setNewest] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/listings", { params: { featured: true, limit: 4 } }).then(r => setFeatured(r.data));
    api.get("/listings", { params: { sort: "ending_soon", status: "live", limit: 8 } }).then(r => setEndingSoon(r.data));
    api.get("/listings", { params: { sort: "newest", limit: 8 } }).then(r => setNewest(r.data));
    api.get("/categories", { params: { top_level: true } }).then(r => {
      const sorted = [...r.data].sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.name.localeCompare(b.name));
      setCategories(sorted);
    });
    api.get("/blog/posts", { params: { limit: 6 } }).then(r => setPosts(r.data));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain-bg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold uppercase tracking-[0.2em]">
              <Sparkles className="h-3 w-3" /> Trusted Auction Marketplace
            </span>
            <h1 data-testid="hero-headline" className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.05] display-serif">
              Bid. <span className="text-amber-500">Win.</span> Own.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed">
              Discover extraordinary items across property, businesses, cars, bikes and more. Compete in transparent auctions with the exact total shown <span className="font-semibold">before</span> you bid.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auctions"><Button data-testid="hero-cta-bid-now" size="lg" className="bg-slate-900 hover:bg-slate-800 text-white gap-2 rounded-full px-6">
                Explore Auctions <ArrowRight className="h-4 w-4" />
              </Button></Link>
              <Link to="/sell"><Button data-testid="hero-cta-sell-free" size="lg" variant="outline" className="rounded-full px-6 border-slate-900 dark:border-slate-100">
                Sell an Item — Free
              </Button></Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="100+" l="Live auctions" />
              <Stat n="₹0" l="Listing fee" />
              <Stat n={`${buyerFee ?? 5}%`} l="Buyer protection" />
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[420px] md:h-[520px]">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85"
                className="col-span-4 row-span-4 rounded-3xl object-cover w-full h-full shadow-2xl" alt="property" />
              <img src="https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?crop=entropy&cs=srgb&fm=jpg&q=85"
                className="col-span-2 row-span-3 rounded-2xl object-cover w-full h-full shadow-xl" alt="bike" />
              <img src="https://images.pexels.com/photos/16524381/pexels-photo-16524381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                className="col-span-2 row-span-3 rounded-2xl object-cover w-full h-full shadow-xl" alt="car" />
              <img src="https://images.unsplash.com/photo-1556740738-b6a63e27c4df?crop=entropy&cs=srgb&fm=jpg&q=85"
                className="col-span-4 row-span-2 rounded-2xl object-cover w-full h-full shadow-xl" alt="business" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Buyer Protection</div>
                <div className="text-sm font-bold">Every winning bid protected</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE CATEGORIES — unified, all equal */}
      <Section eyebrow="Explore Categories" title="Browse the whole marketplace">
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
          data-testid="explore-categories-grid"
        >
          {categories.map(c => (
            <Link
              key={c.id}
              data-testid={`cat-${c.slug}`}
              to={`/auctions?category_id=${c.id}`}
              className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 hover:border-amber-500 hover:shadow-lg transition-all"
            >
              {c.image_url ? (
                <img
                  src={c.image_url}
                  alt={c.name}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition duration-500"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-5xl text-amber-500/30 font-black display-serif">
                  {c.name.charAt(0)}
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent" />
              <div className="absolute inset-x-3 bottom-2.5 text-white">
                <div className="text-sm md:text-base font-bold leading-tight">{c.name}</div>
                <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-slate-300 opacity-0 group-hover:opacity-100 transition">
                  Browse <ArrowRight className="h-2.5 w-2.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* ENDING SOON */}
      <Section
        eyebrow={<><Flame className="h-3 w-3" /> Ending Soon</>}
        title="Bids close in hours"
        cta={{ to: "/auctions?sort=ending_soon", label: "View all ending soon" }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {endingSoon.slice(0, 4).map(l => <AuctionCard key={l.id} listing={l} />)}
        </div>
      </Section>

      {/* FEATURED */}
      <Section eyebrow="Featured Auctions" title="Handpicked for you" cta={{ to: "/auctions?featured=1", label: "View all" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.slice(0, 4).map(l => <AuctionCard key={l.id} listing={l} />)}
        </div>
      </Section>

      {/* NEW */}
      <Section eyebrow="Newly Listed" title="Fresh on the block" cta={{ to: "/auctions?sort=newest", label: "See what's new" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {newest.slice(0, 4).map(l => <AuctionCard key={l.id} listing={l} />)}
        </div>
      </Section>

      {/* BLOG PREVIEW */}
      {posts.length > 0 && (
        <Section eyebrow="Latest from AuctionBulls" title="Guides, insights & news" cta={{ to: "/blog", label: "View all articles" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-blog-preview">
            {posts.slice(0, 3).map(p => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid="home-blog-card"
                    className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all">
                {p.featured_image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.featured_image} className="h-full w-full object-cover group-hover:scale-105 transition duration-500" alt={p.title} />
                  </div>
                )}
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-600">{p.category}</div>
                  <h3 className="mt-2 text-lg font-bold leading-snug line-clamp-2">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-4 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.reading_time} min</span>
                    <span className="ml-auto text-amber-600 font-semibold inline-flex items-center gap-1">Read more <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* PROTECTION */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="rounded-3xl bg-slate-900 text-white p-8 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 grain-bg opacity-40" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400">How AuctionBulls Protects You</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold display-serif">Bid with confidence. See the total. Ship tracked.</h2>
              <p className="mt-4 text-slate-300 max-w-md">Every transaction moves through AuctionBulls escrow. We hold funds until you inspect and accept your item.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Perk icon={ShieldCheck} title="Buyer Protection" text="Escrow release only after your approval." />
              <Perk icon={Truck} title="Tracked Shipping" text="Live tracking on every order." />
              <Perk icon={BadgeCheck} title="Verified Sellers" text="Identity checks on high-value items." />
              <Perk icon={TrendingUp} title="Transparent Fees" text="See exact total before you bid." />
            </div>
          </div>
        </div>
      </section>

      {/* SELL CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Sell With AuctionBulls</div>
            <h2 className="mt-2 text-3xl font-extrabold display-serif">List for free. Pay only when it sells.</h2>
            <p className="mt-2 text-slate-600 dark:text-slate-400 max-w-lg">Reach serious bidders across property, businesses, cars, bikes and more. Transparent 8% success fee. Zero listing costs.</p>
          </div>
          <Link to="/sell"><Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-full px-8">Start Selling</Button></Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="text-2xl font-extrabold text-slate-900 dark:text-white">{n}</div>
      <div className="text-xs text-slate-500 mt-0.5">{l}</div>
    </div>
  );
}
function Section({ eyebrow, title, cta, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-amber-600">{eyebrow}</div>
          <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white display-serif">{title}</h2>
        </div>
        {cta && <Link to={cta.to} className="text-sm font-semibold text-slate-900 dark:text-white hover:text-amber-600 inline-flex items-center gap-1">{cta.label} <ArrowRight className="h-4 w-4" /></Link>}
      </div>
      {children}
    </section>
  );
}
function Perk({ icon: Icon, title, text }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4">
      <Icon className="h-5 w-5 text-amber-400" />
      <div className="mt-3 font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-400 mt-1">{text}</div>
    </div>
  );
}
function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
